import { Cancel, MenuBook } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { endOfDay, startOfDay, subMonths } from 'date-fns';
import * as React from 'react';
import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { MessageDescriptor, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { DoseReportFormat, generateDoseReport } from '../../api/dose-reports';
import { DoseReportContextReadDTO, DoseReportFilterDTO, DoseReportType } from '../../api/dto';
import { DocumentPageStructure } from '../../components/documents/DocumentPageStructure';
import { ResponsiveButton } from '../../components/ResponsiveButton';
import { ValidationErrorsPanel } from '../../components/ValidationErrorsPanel';
import { fetchDimrVersionsByMasterId } from '../../lib/api';
import { FieldsAttributesContextProvider } from '../FieldsAttributesContext';
import { FormGenerator } from '../FormGenerator';
import { PreciseRulesDTO } from '../rule/deserializer';
import { useFormRules } from '../rule/useFormRules';
import { doseReportFormSchema } from '../schemas/documents/doseReport';

type OutputType = 'webView' | 'pdf' | 'excel';

const outputTypes: Record<OutputType, { labelKey: MessageDescriptor['id']; format: DoseReportFormat }> = {
    webView: { labelKey: 'doseReport.format.web', format: 'html' },
    pdf: { labelKey: 'doseReport.format.pdf', format: 'pdf' },
    excel: { labelKey: 'doseReport.format.excel', format: 'xlsx' },
};

const REPORT_FILE_NAME = 'RadiationDoseReport';

const readBlobError = async (error: any, fallback: string): Promise<string> => {
    const data = error?.response?.data;
    if (data instanceof Blob) {
        try {
            const parsed = JSON.parse(await data.text());
            return parsed.message ?? parsed.error ?? fallback;
        } catch {
            return fallback;
        }
    }
    return error?.message ?? fallback;
};

interface RadiationDoseFormProps {
    rules: PreciseRulesDTO<DoseReportContextReadDTO>;
}

export const RadiationDoseForm: React.FC<RadiationDoseFormProps> = ({ rules }) => {
    const intl = useIntl();
    const [searchParams] = useSearchParams();

    const [outputType, setOutputType] = React.useState<OutputType>('webView');
    const [pdfUrl, setPdfUrl] = React.useState<string | null>(null);
    const [htmlUrl, setHtmlUrl] = React.useState<string | null>(null);
    const [isGenerating, setIsGenerating] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const linkedReport = React.useMemo((): Partial<DoseReportFilterDTO> | null => {
        const type = searchParams.get('type') as DoseReportType | null;
        const activityId = Number(searchParams.get('activityId'));
        const participantId = Number(searchParams.get('participantId'));
        const dimrId = Number(searchParams.get('dimrId'));

        switch (type) {
            case DoseReportType.RADIATION_DOSE_REPORT_BY_ACTIVITY:
            case DoseReportType.DAILY_RADIATION_DOSE_REPORT_BY_ACTIVITY:
                return activityId || dimrId
                    ? ({
                          type,
                          ...(activityId ? { activity: { id: activityId } } : {}),
                      } as Partial<DoseReportFilterDTO>)
                    : null;
            case DoseReportType.RADIATION_DOSE_REPORT_BY_PERSON:
                return participantId
                    ? ({ type, participant: { id: participantId } } as Partial<DoseReportFilterDTO>)
                    : null;
            default:
                return null;
        }
    }, []);

    const defaultValues = React.useMemo(
        () =>
            linkedReport ??
            ({
                type: DoseReportType.SUMMARY,
                participant: null,
                participantUnit: null,
                activityResponsibleUnit: null,
                activity: null,
                activityResponsible: null,
                supplier: null,
                dimr: null,
                facility: null,
                startDate: subMonths(new Date(), 3).toISOString(),
                endDate: new Date().toISOString(),
                fromCollectivePercentageTaken: 75,
                fromPercentageTaken: 75,
                fromIndividualHistoDose: 1500,
            } as Partial<DoseReportFilterDTO>),
        [linkedReport]
    );

    const { form, attributes } = useFormRules<DoseReportFilterDTO>({
        formProps: {
            mode: 'onSubmit',
            defaultValues,
        },
        rules,
        originalValues: defaultValues,
        context: { ...rules.context },
    });
    const { handleSubmit, clearErrors } = form;

    const showThresholds = !!attributes.fromPercentageTaken?.editable;

    const uiSchema = React.useMemo(() => doseReportFormSchema(intl, showThresholds), [intl, showThresholds]);

    React.useEffect(
        () => () => {
            if (htmlUrl) URL.revokeObjectURL(htmlUrl);
        },
        [htmlUrl]
    );

    React.useEffect(
        () => () => {
            if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        },
        [pdfUrl]
    );

    const typeValue = form.watch('type');

    const [formKey, setFormKey] = React.useState(0);
    const [isResetting, setIsResetting] = React.useState(false);

    useEffect(() => {
        const fieldsToClean = [
            'participant',
            'activity',
            'activityResponsible',
            'dimr',
            'startDate',
            'endDate',
            'fromPercentageTaken',
            'toPercentageTaken',
            'fromIndividualHistoDose',
            'toIndividualHistoDose',
            'fromCollectivePercentageTaken',
            'toCollectivePercentageTaken',
        ] as const;
        setIsResetting(true);
        fieldsToClean.forEach((f) => {
            if (attributes[f] && !attributes[f].editable) {
                form.resetField(f, { defaultValue: null });
            }
        });
        setFormKey((k) => k + 1);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setIsResetting(false);
            });
        });
    }, [typeValue]);

    const reset = () => {
        form.reset(defaultValues);
        setFormKey((k) => k + 1);
        setHtmlUrl(null);
        setPdfUrl(null);
        setError(null);
    };

    const generate = async (filter: DoseReportFilterDTO) => {
        if (isGenerating) return;

        setError(null);
        setIsGenerating(true);

        try {
            const blob = await generateDoseReport({ format: outputTypes[outputType].format, filter });
            const url = URL.createObjectURL(blob);

            if (outputType === 'webView') {
                setHtmlUrl(url);
            } else if (outputType === 'pdf') {
                setPdfUrl(url);
                window.open(url, '_blank');
            } else {
                const link = document.createElement('a');
                link.href = url;
                link.download = `${REPORT_FILE_NAME}.xlsx`;
                link.click();
                URL.revokeObjectURL(url);
            }
        } catch (e) {
            const fallback = intl.formatMessage({ id: 'doseReport.generate.error' });
            console.error(await readBlobError(e, fallback));
            setError(fallback);
        } finally {
            setIsGenerating(false);
        }
    };

    const onGenerateClick = () => {
        clearErrors();
        void handleSubmit((values) => generate(values as DoseReportFilterDTO))();
    };

    const hasAutoGenerated = React.useRef(false);

    React.useEffect(() => {
        if (!linkedReport || hasAutoGenerated.current) return;

        const dimrId = searchParams.get('dimrId');

        const run = async () => {
            let values = { ...form.getValues(), ...linkedReport } as DoseReportFilterDTO;

            if (dimrId) {
                const results = await fetchDimrVersionsByMasterId(Number(dimrId));
                if (results.length) {
                    const latestDimr = results.reduce((acc, current) =>
                        current.versionNumber > acc.versionNumber ? current : acc
                    );
                    values = { ...values, dimr: latestDimr };
                    form.reset(values);
                }
            }

            hasAutoGenerated.current = true;
            void generate(values);
        };

        void run();
    }, [linkedReport]);

    const centerReport = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
        const doc = e.currentTarget.contentDocument;
        if (!doc) return;

        const style = doc.createElement('style');
        // Pentaho emits a fixed-width table anchored top-left; nudge it into the middle.
        style.textContent = `
            body { display: flex; justify-content: center; margin: 0; padding: 1rem 0; }
            body > * { width: 100% !important; margin-left: auto !important; margin-right: auto !important; }
        `;
        doc.head.appendChild(style);
    };

    const startDate = form.watch('startDate');
    const endDate = form.watch('endDate');

    useEffect(() => {
        if (startDate) {
            form.setValue('startDate', startOfDay(new Date(startDate)).toISOString());
        }
    }, [startDate]);

    useEffect(() => {
        if (endDate) {
            form.setValue('endDate', endOfDay(new Date(endDate)).toISOString());
        }
    }, [endDate]);

    return (
        <FormProvider {...form}>
            <DocumentPageStructure
                isLoading={isGenerating}
                Header={
                    <Box sx={{ marginLeft: 1 }}>
                        <Typography>{intl.formatMessage({ id: 'doseReport.title' })}</Typography>
                    </Box>
                }
                Body={
                    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Alert severity="info" sx={{ width: '100%' }}>
                            {intl.formatMessage({ id: 'doseReport.info' })}
                        </Alert>

                        <ValidationErrorsPanel schema={uiSchema} />

                        <FieldsAttributesContextProvider value={attributes}>
                            <Box sx={{ visibility: isResetting ? 'hidden' : 'visible' }}>
                                <FormGenerator
                                    key={formKey}
                                    rootElement={uiSchema}
                                    form={form}
                                    disabled={isGenerating}
                                />
                            </Box>
                        </FieldsAttributesContextProvider>

                        <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                            <Stack direction="row" spacing={1}>
                                {(Object.keys(outputTypes) as OutputType[]).map((output) => (
                                    <Button
                                        key={output}
                                        onClick={() => setOutputType(output)}
                                        variant={outputType === output ? 'contained' : 'outlined'}
                                        disabled={isGenerating}
                                    >
                                        {intl.formatMessage({ id: outputTypes[output].labelKey })}
                                    </Button>
                                ))}
                            </Stack>

                            <Box sx={{ flexGrow: 1 }} />

                            <Stack direction="row" spacing={1}>
                                <ResponsiveButton icon={<Cancel />} onClick={reset} disabled={isGenerating}>
                                    {intl.formatMessage({ id: 'common.reset' })}
                                </ResponsiveButton>
                                <ResponsiveButton
                                    icon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <MenuBook />}
                                    onClick={onGenerateClick}
                                    disabled={isGenerating}
                                >
                                    {intl.formatMessage({ id: 'doseReport.generate' })}
                                </ResponsiveButton>
                            </Stack>
                        </Stack>

                        {error ? (
                            <Alert severity="error" sx={{ width: '100%', marginTop: 2 }}>
                                {error}
                            </Alert>
                        ) : null}

                        {/*pdfUrl ? (
                            <Alert
                                severity="success"
                                sx={{ width: '100%', marginTop: 2 }}
                                action={
                                    <Button color="inherit" size="small" onClick={() => window.open(pdfUrl, '_blank')}>
                                        Open PDF
                                    </Button>
                                }
                            >
                                Your report is ready
                            </Alert>
                        ) : null*/}

                        {htmlUrl ? (
                            <Box
                                component="iframe"
                                src={htmlUrl}
                                onLoad={centerReport}
                                title={intl.formatMessage({ id: 'doseReport.title' })}
                                sx={{ width: '100%', height: '75vh', border: 0, marginTop: 2 }}
                            />
                        ) : null}
                    </Box>
                }
            />
        </FormProvider>
    );
};
